import type {
  SupplyItemId, NoteId, TaskId, WikiPageId, ChecklistId, ChecklistRunId,
  MemberId, PersonId, RoomId,
} from '../ids.js'
import type { DayKey } from '../time.js'

export type SupplyState = 'ok' | 'low' | 'out'

export interface SupplyItem {
  id: SupplyItemId
  name: string
  category: string
  icon: string | null
  state: SupplyState
  lastFlaggedAt: number | null
  lastFlaggedBy: PersonId | null
  lastRestockedAt: number | null
  active: boolean
}

export interface SupplyEvent {
  id: string
  itemId: SupplyItemId
  state: SupplyState
  at: number
  by: PersonId
}

export interface Note {
  id: NoteId
  authorId: PersonId
  body: string
  colour: string
  /** Free-floating position on the board, when not pinned to a room. */
  position: { x: number; y: number } | null
  pinnedToRoomId: RoomId | null
  createdAt: number
  expiresAt: number | null
}

export type TaskStatus = 'todo' | 'doing' | 'done'

export interface Task {
  id: TaskId
  title: string
  details: string | null
  createdBy: MemberId
  /** Null = unassigned pool; this is what interns pull from. */
  assignedTo: MemberId | null
  dueDay: DayKey | null
  status: TaskStatus
  priority: 0 | 1 | 2
  createdAt: number
  completedAt: number | null
}

export interface WikiPage {
  id: WikiPageId
  title: string
  slug: string
  body: string
  category: string
  updatedAt: number
  updatedBy: MemberId
  /** Renders a QR code for guests to scan. */
  isWifiPage: boolean
}

export type ChecklistKind = 'opening' | 'closing'

export interface ChecklistItem { id: string; label: string; order: number }

export interface Checklist {
  id: ChecklistId
  kind: ChecklistKind
  items: ChecklistItem[]
}

export interface ChecklistRun {
  id: ChecklistRunId
  checklistId: ChecklistId
  day: DayKey
  triggeredBy: PersonId
  startedAt: number
  itemsDone: { itemId: string; doneAt: number; by: PersonId }[]
  completedAt: number | null
  skippedAt: number | null
}

/**
 * The clock cannot tell whether the place is currently an office or a games
 * room, so the mode is explicit (docs/01 §9.1). 'empty' is inferred.
 */
export type SpaceModeValue = 'work' | 'chill' | 'gaming' | 'empty'

export interface SpaceMode {
  current: SpaceModeValue
  setBy: PersonId | null
  setAt: number
  /** True when derived from presence rather than set by a human. */
  auto: boolean
}

export interface RamadanPeriod {
  year: number
  startDay: DayKey
  endDay: DayKey
  source: 'manual' | 'calculated'
  imsakOffsetMinutes: number
}

export interface Settings {
  dayRolloverHour: number
  weekStartDow: number
  purchaseThreshold: number
  autoApproveHours: number
  recapWeekday: number
  recapHour: number
  qiblaMode: 'auto' | 'fixed'
  qiblaFixedAngleDeg: number
  guestArrivingLeadMinutes: number
  queueClaimWindowMinutes: number
  faceCooldownSeconds: number
  faceMatchThreshold: number
  faceFramesToAgree: number
  latitude: number
  longitude: number
}

export const DEFAULT_SETTINGS: Settings = {
  dayRolloverHour: 5,
  weekStartDow: 6,
  purchaseThreshold: 10_000, // 100 EGP in piastres
  autoApproveHours: 4,
  recapWeekday: 5, // Friday
  recapHour: 21,
  qiblaMode: 'auto',
  qiblaFixedAngleDeg: 137.2226,
  guestArrivingLeadMinutes: 15,
  queueClaimWindowMinutes: 5,
  faceCooldownSeconds: 300,
  faceMatchThreshold: 0.4,
  faceFramesToAgree: 3,
  latitude: 30.031244,
  longitude: 31.549313,
}
